<template>
  <div class="order-detail">
    <el-descriptions :column="2" border>
      <el-descriptions-item label="工单号">
        <span class="order-id">#{{ order.id }}</span>
      </el-descriptions-item>
      <el-descriptions-item label="状态">
        <el-tag :type="getStatusType(order.status)">
          {{ getStatusText(order.status) }}
        </el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="家电类型">
        {{ getApplianceTypeName(order.appliance_type) }}
      </el-descriptions-item>
      <el-descriptions-item label="品牌型号">
        {{ order.brand_model }}
      </el-descriptions-item>
      <el-descriptions-item label="紧急程度">
        <el-tag :type="getUrgencyType(order.urgency)" size="small">
          {{ getUrgencyText(order.urgency) }}
        </el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="购买时间">
        {{ formatDate(order.purchase_date) }}
      </el-descriptions-item>
      <el-descriptions-item label="联系人">
        {{ order.contact_name }}
      </el-descriptions-item>
      <el-descriptions-item label="联系电话">
        {{ order.contact_phone }}
      </el-descriptions-item>
      <el-descriptions-item label="维修师傅" v-if="order.worker_name">
        {{ order.worker_name }}
      </el-descriptions-item>
      <el-descriptions-item label="师傅电话" v-if="order.worker_phone">
        {{ order.worker_phone }}
      </el-descriptions-item>
      <el-descriptions-item label="创建时间">
        {{ formatDate(order.created_at) }}
      </el-descriptions-item>
      <el-descriptions-item label="预约时间">
        {{ formatDate(order.preferred_date) }} {{ getTimeSlotText(order.preferred_time) }}
      </el-descriptions-item>
      <el-descriptions-item label="维修费用" v-if="order.service_fee">
        <span class="service-fee">¥{{ order.service_fee }}</span>
      </el-descriptions-item>
      <el-descriptions-item label="支付方式" v-if="order.payment_method">
        <el-tag :type="getPaymentMethodType(order.payment_method)">
          {{ getPaymentMethodName(order.payment_method) }}
        </el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="收款状态" v-if="order.payment_status">
        <el-tag :type="order.payment_status === 'completed' ? 'success' : 'warning'">
          {{ order.payment_status === 'completed' ? '已收款' : '待收款' }}
        </el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="实际收款" v-if="order.actual_payment">
        <span class="actual-payment">¥{{ order.actual_payment }}</span>
      </el-descriptions-item>
    </el-descriptions>
    
    <el-divider content-position="left">详细地址</el-divider>
    <p class="address">{{ order.address }}</p>
    
    <el-divider content-position="left">故障描述</el-divider>
    <p class="description">{{ order.problem_description }}</p>
    
    <el-divider content-position="left" v-if="order.remarks">备注信息</el-divider>
    <p class="remarks" v-if="order.remarks">{{ order.remarks }}</p>
    
    <el-divider content-position="left" v-if="order.images && order.images.length > 0">故障图片</el-divider>
    <div class="images" v-if="order.images && order.images.length > 0">
      <el-image
        v-for="(image, index) in order.images"
        :key="index"
        :src="image"
        :preview-src-list="order.images"
        class="image-item"
        fit="cover"
      ></el-image>
    </div>
    
    <!-- 维修进度 -->
    <el-divider content-position="left" v-if="order.progress && order.progress.length > 0">维修进度</el-divider>
    <el-timeline v-if="order.progress && order.progress.length > 0">
      <el-timeline-item
        v-for="(item, index) in order.progress"
        :key="index"
        :timestamp="formatDate(item.created_at)"
        placement="top"
      >
        <el-card>
          <h4>{{ item.title }}</h4>
          <p>{{ item.description }}</p>
          <div v-if="item.images && item.images.length > 0" class="progress-images">
            <el-image
              v-for="(img, imgIndex) in item.images"
              :key="imgIndex"
              :src="img"
              :preview-src-list="item.images"
              class="progress-image"
              fit="cover"
            ></el-image>
          </div>
        </el-card>
      </el-timeline-item>
    </el-timeline>
    
    <!-- 操作按钮 -->
    <div class="actions" v-if="order.status === 'pending'">
      <el-button type="danger" @click="cancelOrder">取消工单</el-button>
    </div>
    
    <div class="actions" v-if="order.status === 'completed'">
      <el-rate
        v-model="rating"
        :disabled="order.rating > 0"
        show-text
        :texts="['极差', '失望', '一般', '满意', '惊喜']"
      ></el-rate>
      <el-input
        v-if="!order.rating"
        v-model="comment"
        type="textarea"
        placeholder="请对本次服务进行评价"
        :rows="3"
        maxlength="200"
        show-word-limit
        style="margin-top: 10px;"
      ></el-input>
      <el-button 
        v-if="!order.rating" 
        type="primary" 
        @click="submitRating"
        style="margin-top: 10px;"
      >
        提交评价
      </el-button>
      <div v-if="order.rating" class="existing-rating">
        <p><strong>您的评价：</strong></p>
        <el-rate :value="order.rating" disabled show-score></el-rate>
        <p v-if="order.comment">{{ order.comment }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OrderDetail',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      rating: 0,
      comment: ''
    }
  },
  methods: {
    async cancelOrder() {
      try {
        await this.$confirm('确定要取消这个工单吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        // 调用取消工单API
        this.$message.success('工单已取消')
        this.$emit('refresh')
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('取消工单失败')
        }
      }
    },
    
    async submitRating() {
      if (this.rating === 0) {
        this.$message.warning('请选择评分')
        return
      }
      
      try {
        // 调用评价API
        this.$message.success('评价提交成功')
        this.$emit('refresh')
      } catch (error) {
        this.$message.error('评价提交失败')
      }
    },
    
    getApplianceTypeName(type) {
      const typeMap = {
        'washing_machine': '洗衣机',
        'refrigerator': '冰箱',
        'air_conditioner': '空调',
        'television': '电视',
        'microwave': '微波炉',
        'water_heater': '热水器',
        'range_hood': '油烟机',
        'gas_stove': '燃气灶',
        'other': '其他'
      }
      return typeMap[type] || type
    },
    
    getStatusType(status) {
      const statusMap = {
        'pending': 'warning',
        'accepted': 'info',
        'in_progress': 'primary',
        'completed': 'success',
        'cancelled': 'danger'
      }
      return statusMap[status] || 'info'
    },
    
    getStatusText(status) {
      const statusMap = {
        'pending': '待接单',
        'accepted': '已接单',
        'in_progress': '维修中',
        'completed': '已完成',
        'cancelled': '已取消'
      }
      return statusMap[status] || '未知状态'
    },
    
    getUrgencyType(urgency) {
      const urgencyMap = {
        'low': 'info',
        'medium': 'warning',
        'high': 'danger'
      }
      return urgencyMap[urgency] || 'info'
    },
    
    getUrgencyText(urgency) {
      const urgencyMap = {
        'low': '一般',
        'medium': '紧急',
        'high': '非常紧急'
      }
      return urgencyMap[urgency] || '一般'
    },
    
    getTimeSlotText(timeSlot) {
      const slotMap = {
        'morning': '上午 (9:00-12:00)',
        'afternoon': '下午 (13:00-17:00)',
        'evening': '晚上 (18:00-20:00)'
      }
      return slotMap[timeSlot] || ''
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    },
    
    getPaymentMethodType(method) {
      const typeMap = {
        'wechat_pay': 'success',
        'cash': 'warning',
        'alipay': 'primary',
        'customer_service': 'info'
      }
      return typeMap[method] || 'info'
    },
    
    getPaymentMethodName(method) {
      const nameMap = {
        'wechat_pay': '微信支付',
        'cash': '现金支付',
        'alipay': '支付宝支付',
        'customer_service': '客户对接客服'
      }
      return nameMap[method] || method
    }
  }
}
</script>

<style scoped>
.order-detail {
  padding: 20px 0;
}

.order-id {
  font-weight: 600;
  color: #409EFF;
}

.address, .description, .remarks {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  line-height: 1.6;
  margin: 10px 0;
}

.images {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 10px 0;
}

.image-item {
  width: 100px;
  height: 100px;
  border-radius: 4px;
}

.progress-images {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.progress-image {
  width: 60px;
  height: 60px;
  border-radius: 4px;
}

.service-fee {
  font-weight: bold;
  color: #E6A23C;
}

.actual-payment {
  font-weight: bold;
  color: #67C23A;
}

.actions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.existing-rating {
  margin-top: 10px;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .order-detail {
    padding: 15px 0;
  }
  
  .address, .description, .remarks {
    padding: 12px;
    margin: 8px 0;
    font-size: 0.9rem;
  }
  
  .images {
    gap: 8px;
    margin: 8px 0;
  }
  
  .image-item {
    width: 80px;
    height: 80px;
  }
  
  .progress-images {
    gap: 8px;
    margin-top: 8px;
  }
  
  .progress-image {
    width: 50px;
    height: 50px;
  }
  
  .actions {
    margin-top: 15px;
    padding-top: 15px;
  }
  
  .existing-rating {
    margin-top: 8px;
    padding: 12px;
    font-size: 0.9rem;
  }
  
  /* 移动端描述列表优化 */
  .el-descriptions {
    font-size: 0.9rem;
  }
  
  .el-descriptions__label {
    font-size: 0.85rem;
  }
  
  .el-descriptions__content {
    font-size: 0.9rem;
  }
  
  /* 移动端标签优化 */
  .el-tag {
    font-size: 0.8rem;
    padding: 4px 8px;
  }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
  .order-detail {
    padding: 10px 0;
  }
  
  .address, .description, .remarks {
    padding: 10px;
    margin: 6px 0;
    font-size: 0.85rem;
  }
  
  .images {
    gap: 6px;
    margin: 6px 0;
  }
  
  .image-item {
    width: 70px;
    height: 70px;
  }
  
  .progress-images {
    gap: 6px;
    margin-top: 6px;
  }
  
  .progress-image {
    width: 45px;
    height: 45px;
  }
  
  .actions {
    margin-top: 12px;
    padding-top: 12px;
  }
  
  .existing-rating {
    margin-top: 6px;
    padding: 10px;
    font-size: 0.85rem;
  }
  
  .el-descriptions {
    font-size: 0.85rem;
  }
  
  .el-descriptions__label {
    font-size: 0.8rem;
  }
  
  .el-descriptions__content {
    font-size: 0.85rem;
  }
  
  .el-tag {
    font-size: 0.75rem;
    padding: 3px 6px;
  }
}
</style>
